import { Collection } from 'firestorter';

import Collections from '../../utils/collections';
import BaseDocument from '../../Model/BaseDocument';

/**
 * Recovery snapshot for the bulk exercise creator. Doc ids are auto-generated;
 * each doc belongs to a (coachId, createdBy) pair — the coach the exercises
 * are for and the logged-in user (coach or AC) who was creating them. Holds
 * serialized card metadata so a batch interrupted by a closed browser can be
 * restored — see BulkExerciseCreator/useSessionPersistence.
 *
 * Finished sessions are kept (marked isCompleted) rather than deleted, so the
 * docs double as a history of bulk-creation batches; only the single
 * incomplete session per (coachId, createdBy) is ever offered for recovery.
 */
class BulkExerciseCreateSession extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.BULK_EXERCISE_CREATE_SESSION}/${id}`, opts);
  }

  get autoSave() {
    return !!this.data.autoSave;
  }

  get cards() {
    return this.data.cards || [];
  }

  static async getByCoachAndCreator(coachId, createdBy) {
    const sessionsCollection = new Collection(Collections.BULK_EXERCISE_CREATE_SESSION, {
      createDocument: ({ id }, opts) => new BulkExerciseCreateSession(id, opts),
      query: (ref) => ref
        .where('coachId', '==', coachId)
        .where('createdBy', '==', createdBy)
        .where('isCompleted', '==', false)
        .limit(1),
    });
    await sessionsCollection.fetch();
    return sessionsCollection.docs.length ? sessionsCollection.docs[0] : null;
  }

  static async create({
    coachId, createdBy, autoSave, cards,
  }) {
    const sessionsCollection = new Collection(Collections.BULK_EXERCISE_CREATE_SESSION);
    const { id } = await sessionsCollection.add({
      coachId, createdBy, autoSave, cards, isCompleted: false, savedAt: new Date(),
    });
    return id;
  }

  static async update(sessionId, { autoSave, cards }) {
    const sessionDoc = new BulkExerciseCreateSession(sessionId);
    await sessionDoc.init();
    await sessionDoc.updateFields({ autoSave, cards, savedAt: new Date() });
  }

  // Marks a session as finished. The doc is kept as a record of the batch —
  // it just stops matching the recovery query above.
  static async completeById(sessionId) {
    const sessionDoc = new BulkExerciseCreateSession(sessionId);
    await sessionDoc.init();
    if (sessionDoc.exists) {
      await sessionDoc.updateFields({ isCompleted: true, completedAt: new Date() });
    }
  }
}

export default BulkExerciseCreateSession;
